import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SEO, { getOrganizationSchema, getWebSiteSchema, getFAQSchema } from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const homeFAQ = [
  {
    question: '年轻的母亲这个平台适合什么样的妈妈？',
    answer: '无论你是正在备孕的准妈妈、刚迎来宝宝的产后妈妈，还是正在育儿路上的年轻母亲，只要你在 20-35 岁之间，希望获得科学育儿指导、心理支持和自我提升建议，这个平台都欢迎你加入。',
  },
  {
    question: '如何加入年轻母亲成长社群？',
    answer: '你可以通过页面底部的订阅表单留下邮箱，我们会定期发送育儿资讯和社群活动信息。同时，你也可以在每篇文章下方留言，与其他妈妈交流互动。',
  },
  {
    question: '平台上的育儿建议是否科学可靠？',
    answer: '我们的文章由资深母婴编辑团队撰写，参考权威医学文献和儿童心理学研究，同时邀请儿科医生、心理咨询师进行专业审核，确保每一条建议都有科学依据。',
  },
  {
    question: '产后恢复多久能回到产前状态？',
    answer: '每个人的恢复速度不同，一般建议给自己 6 到 12 个月的时间。重要的是循序渐进，不要急于求成。我们的产后恢复专栏提供了科学的分阶段恢复方案，帮助你安全有效地恢复身体。',
  },
  {
    question: '如何应对产后的情绪波动？',
    answer: '产后情绪波动非常常见，约有 50%-80% 的新妈妈会经历「产后情绪低落」。首先要接纳自己的情绪，其次可以通过保证睡眠、适当运动、与伴侣沟通来调节。如果情绪低落持续超过两周，建议寻求专业心理咨询帮助。',
  },
];

export default function Home() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const featuredArticles = articles.slice(0, 6);

  const orgSchema = getOrganizationSchema();
  const webSiteSchema = getWebSiteSchema();
  const faqSchema = getFAQSchema(homeFAQ);

  return (
    <>
      <SEO
        schema={[orgSchema, webSiteSchema, faqSchema]}
      />
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20and%20artistic%20illustration%20of%20a%20young%20mother%20gently%20holding%20her%20baby%20with%20soft%20flowing%20fabrics%20in%20warm%20peach%20cream%20and%20golden%20tones%2C%20abstract%20floral%20elements%2C%20dreamy%20soft%20focus%20background%2C%20nurturing%20and%20peaceful%20mood%2C%20watercolor%20style%20blend%2C%20elegant%20composition&width=1600&height=800&seq=hero-mother-2026&orientation=landscape"
            alt="年轻的母亲与宝宝温馨画面"
            title="年轻的母亲与宝宝"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/40"></div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-5 leading-tight">
              年轻的母亲
            </h1>
            <p className="text-base md:text-lg text-white/90 max-w-2xl mx-auto mb-8 leading-relaxed">
              每一位年轻的母亲都值得在陪伴孩子成长的同时，活出自己最好的状态。
              我们提供科学育儿知识、心理调适建议与产后恢复方案，陪你走过这段温暖而充满挑战的旅程。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/xin-li-tiao-shi"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                开始探索
                <i className="ri-arrow-right-line"></i>
              </Link>
              <a
                href="#categories"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-background-50/20 backdrop-blur-sm text-white font-medium text-sm hover:bg-background-50/30 transition-colors cursor-pointer whitespace-nowrap"
              >
                浏览分类
                <i className="ri-arrow-down-line"></i>
              </a>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section id="categories" className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-3">
                探索成长主题
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto text-sm md:text-base">
                从心理调适到科学育儿，我们为你准备了全方位的内容支持
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center text-center p-5 rounded-lg bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3 group-hover:bg-primary-200 transition-colors">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1 whitespace-nowrap">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
                    {cat.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10 gap-4">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-2">
                  精选文章
                </h2>
                <p className="text-sm text-foreground-600">
                  编辑团队精心挑选的高质量育儿内容
                </p>
              </div>
              <Link
                to="/category/ke-xue-yu-er"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredArticles.map((article) => {
                const cat = categories.find((c) => c.slug === article.category);
                return (
                  <ArticleCard
                    key={article.slug}
                    slug={article.slug}
                    title={article.title}
                    excerpt={article.excerpt}
                    coverImage={article.coverImage}
                    category={article.category}
                    categoryName={cat?.name || article.category}
                    readTime={article.readTime}
                    publishedAt={article.publishedAt}
                  />
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-foreground-600 text-sm md:text-base">
                你关心的问题，我们都有答案
              </p>
            </div>
            <div className="space-y-4">
              {homeFAQ.map((faq, index) => (
                <details
                  key={index}
                  className="group bg-background-100 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer"
                >
                  <summary className="flex items-center justify-between px-5 py-4 text-sm md:text-base font-medium text-foreground-800 list-none">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 bg-accent-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-4">
              加入年轻母亲成长社群
            </h2>
            <p className="text-foreground-700 mb-8 text-sm md:text-base leading-relaxed max-w-lg mx-auto">
              每周获取精选育儿文章、实用技巧和社群活动信息。与数万名年轻妈妈一起，在育儿路上彼此支持、共同成长。
            </p>
            <form
              data-readdy-form
              id="home-newsletter"
              action="https://readdy.ai/api/form/d9spc314ne17ei0sdlkg"
              method="POST"
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <input
                type="email"
                name="email"
                placeholder="输入你的邮箱地址"
                required
                className="flex-1 px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
              />
              <button
                type="submit"
                className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                免费订阅
              </button>
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-honeypot"
              />
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}